import React from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Dropdown,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  Option,
  Tooltip,
} from "@mui/joy";
import SearchBar from "../../../common/SearchBar";
import DarkSelect from "../../../common/DarkSelect";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import { FilterListIcon, SUserAdd } from "../../../../styles/StyledMUI";
import {
  colorTokens,
  hrCardSx,
  hrFilterSelectSx,
  hrTooltipSurfaceSx,
  profileFormFieldLabelSx,
  textTokens,
  withAlpha,
} from "../../../../styles/globalStyles";

const ALL_QUALS = "ALL" as const;

type CustomersPageToolbarProps = {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  qualificationFilter: string;
  setQualificationFilter: React.Dispatch<React.SetStateAction<string>>;
  qualificationOptions: string[];
  canCreate: boolean;
  onOpenCreate: () => void;
};

export default function CustomersPageToolbar({
  searchQuery,
  setSearchQuery,
  qualificationFilter,
  setQualificationFilter,
  qualificationOptions,
  canCreate,
  onOpenCreate,
}: CustomersPageToolbarProps) {
  const sortedSigs = React.useMemo(
    () => [...qualificationOptions].sort((a, b) => a.localeCompare(b, "fr")),
    [qualificationOptions],
  );

  const hasActiveFilters = searchQuery.trim().length > 0 || qualificationFilter !== "";
  const qualMenuActive = qualificationFilter !== "";

  const [filterOpen, setFilterOpen] = React.useState(false);
  const filterButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const filterMenuRef = React.useRef<HTMLDivElement | null>(null);

  const handleFilterOpenChange = React.useCallback(
    (_event: React.SyntheticEvent | null, isOpen: boolean) => {
      setFilterOpen(isOpen);
    },
    [],
  );

  React.useEffect(() => {
    if (!filterOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (filterMenuRef.current?.contains(target)) return;
      if (filterButtonRef.current?.contains(target)) return;
      setFilterOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown as unknown as EventListener);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown as unknown as EventListener);
    };
  }, [filterOpen]);

  const clearFilters = React.useCallback(() => {
    setSearchQuery("");
    setQualificationFilter("");
  }, [setSearchQuery, setQualificationFilter]);

  return (
    <Card
      sx={{
        p: { xs: 0.75, sm: 1 },
        mb: 1,
        maxWidth: "min(95vw, 640px)",
        width: "100%",
        alignSelf: "center",
        ...hrCardSx,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: { xs: 0.75, sm: 1 },
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SearchBar
            value={searchQuery}
            onValueChange={setSearchQuery}
            fullWidth
            placeholder="Rechercher…"
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Dropdown open={filterOpen} onOpenChange={handleFilterOpenChange}>
            <Tooltip title="Filtres" placement="top">
              <MenuButton
                ref={filterButtonRef}
                slots={{ root: IconButtonCTA }}
                slotProps={{
                  root: {
                    size: "sm",
                    "aria-label": "Ouvrir le menu des filtres",
                    ctaVariant: "accent",
                  },
                }}
                sx={{ border: "none" }}
              >
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <FilterListIcon sx={{ color: textTokens.color.primary }} />
                  {qualMenuActive ? (
                    <Box
                      aria-hidden
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: colorTokens.status.success,
                        boxShadow: `0 0 0 2px ${colorTokens.bg.surface}`,
                      }}
                    />
                  ) : null}
                </Box>
              </MenuButton>
            </Tooltip>
            <Menu
              ref={filterMenuRef}
              placement="bottom-end"
              sx={{
                ...hrTooltipSurfaceSx,
                minWidth: 260,
                maxWidth: "min(95vw, 360px)",
                overflow: "visible",
                p: 0,
                zIndex: 1300,
              }}
            >
              <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
                {sortedSigs.length > 0 ? (
                  <FormControl>
                    <FormLabel sx={{ ...profileFormFieldLabelSx, mb: 0.5 }}>Qualif.</FormLabel>
                    <DarkSelect
                      id="customer-qualification-filter"
                      aria-label="Qualification"
                      placeholder="Toutes"
                      value={qualificationFilter === "" ? ALL_QUALS : qualificationFilter}
                      onChange={(_e, v) => {
                        if (v == null || v === ALL_QUALS) setQualificationFilter("");
                        else setQualificationFilter(String(v));
                      }}
                      sx={{ ...hrFilterSelectSx, color: textTokens.color.primary }}
                      slotProps={{ listbox: { disablePortal: true } }}
                    >
                      <Option value={ALL_QUALS}>Toutes</Option>
                      {sortedSigs.map((sig) => (
                        <Option key={sig} value={sig}>
                          {sig}
                        </Option>
                      ))}
                    </DarkSelect>
                  </FormControl>
                ) : (
                  <DarkTypography level="body-xs" colorVariant="muted">
                    Aucune qualification
                  </DarkTypography>
                )}

                {hasActiveFilters && (
                  <>
                    <Divider
                      sx={{ borderColor: withAlpha(textTokens.color.primary, 0.12), my: 0.25 }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="soft"
                      color="neutral"
                      onClick={clearFilters}
                      sx={{ fontWeight: 600 }}
                    >
                      Tout effacer
                    </Button>
                  </>
                )}
              </Box>
            </Menu>
          </Dropdown>

          {canCreate && (
            <Tooltip title="Nouveau client" placement="top">
              <IconButtonCTA
                ctaVariant="accent"
                size="sm"
                variant="plain"
                aria-label="Nouveau client"
                onClick={onOpenCreate}
              >
                <SUserAdd />
              </IconButtonCTA>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Card>
  );
}
